import { theme } from '@prophecy/theme';
import { Stack } from '@prophecy/ui-v3';
import styled, { css } from 'styled-components';

import { CapacityStatus } from './types';

const statusBorder: Record<CapacityStatus, string> = {
  onTrack: theme.colors.gray200,
  runningOutFast: theme.colors.warning400,
  exhausted: theme.colors.error300
};

const statusBanner: Record<CapacityStatus, ReturnType<typeof css>> = {
  onTrack: css`
    background: ${theme.colors.gray50};
    border-top: 1px solid ${theme.colors.gray200};
  `,
  runningOutFast: css`
    background: ${theme.colors.warning50};
    border-top: 1px solid ${theme.colors.warning300};
  `,
  exhausted: css`
    background: ${theme.colors.error50};
    border-top: 1px solid ${theme.colors.error300};
  `
};

export const SurfaceCard = styled.div`
  border: 1px solid ${theme.colors.gray200};
  border-radius: ${theme.radius.x8};
  background: ${theme.colors.white};
  overflow: hidden;
`;

export const CapacityCard = styled(SurfaceCard)<{ $status: CapacityStatus }>`
  border-color: ${({ $status }) => statusBorder[$status]};
`;

export const CapacityBanner = styled.div<{ $status: CapacityStatus }>`
  padding: ${theme.spaces.x16} ${theme.spaces.x20};
  ${({ $status }) => statusBanner[$status]}
`;

export const CardPadding = styled(Stack)`
  padding: ${theme.spaces.x20};
`;

export const CompactCardPadding = styled(Stack)`
  padding: ${theme.spaces.x16};
`;

export const UsedSummary = styled(Stack)`
  flex: none;
  min-width: 79px;
  padding-right: ${theme.spaces.x12};
  border-right: 1px solid ${theme.colors.gray300};
`;

export const MetricCard = styled(SurfaceCard)`
  flex: 1 1 0;
  min-width: 0;
`;

export const MetricHeaderButton = styled.button`
  all: unset;
  box-sizing: border-box;
  display: flex;
  width: 100%;
  align-items: center;
  gap: ${theme.spaces.x8};
  padding: ${theme.spaces.x16} ${theme.spaces.x16} 0;
  cursor: pointer;

  &:focus-visible {
    outline: none;
    box-shadow: ${theme.focusRing.shadow.xs};
  }
`;

export const Dot = styled.span<{ $color: string; $size?: string }>`
  flex: none;
  width: ${({ $size }) => $size || theme.sizes.x8};
  height: ${({ $size }) => $size || theme.sizes.x8};
  border-radius: ${theme.radius.full};
  background: ${({ $color }) => $color};
`;

export const DividerLine = styled.hr`
  border: none;
  border-top: 1px solid ${theme.colors.gray200};
  margin: 0;
`;

export const EnvironmentRow = styled.button`
  all: unset;
  box-sizing: border-box;
  display: flex;
  width: 100%;
  align-items: center;
  gap: ${theme.spaces.x12};
  padding: ${theme.spaces.x6} 0;
  cursor: pointer;
  border-radius: ${theme.radius.x4};

  &:focus-visible {
    outline: none;
    box-shadow: ${theme.focusRing.shadow.xs};
  }

  &:hover {
    background: ${theme.colors.gray50};
  }
`;

export const NoteBox = styled.div`
  padding: ${theme.spaces.x12} ${theme.spaces.x16};
  border: 1px solid ${theme.colors.gray200};
  border-radius: ${theme.radius.x8};
  background: ${theme.colors.gray50};
`;

export const AdminOnlyRule = styled.div`
  flex: 1;
  height: 1px;
  background: ${theme.colors.gray200};
`;

export const StatCard = styled(SurfaceCard)`
  flex: 1 1 0;
  min-width: 0;
  padding: ${theme.spaces.x16};
`;

export const LoadingBlock = styled.div`
  width: 100%;
  height: ${theme.sizes.x64};
  border-radius: ${theme.radius.x8};
  background: ${theme.colors.gray100};
`;

export const BarLabel = styled.div`
  flex: none;
  width: 117px;
`;

/** Fixed so both bars end at the same x regardless of how wide their percentage reads. */
export const BarPercent = styled.div`
  flex: none;
  width: 36px;
  text-align: right;
`;
